import { useCallback, useMemo } from 'react';

import {
  createSpec,
  specTypes,
  useFetch,
  useDataRefId,
  useRefetch,
  useMutation,
} from '@bingads-webui-react/overreact';

import { environmentLookupFn } from '../../../environment/environment-instance';
import {
  fetchRequestContract,
  fetchResponseContract,
} from '../spec/fetch';

import {
  mutationRequestContract,
  mutationResponseContract,
} from '../spec/mutation';
import type {
  Account,
  AccountMutationData,
  AccountVariables,
  FetchRefetchMutationConfig,
  MutationRequestConfig,
  ReadParameters,
  ReadRequestConfig,
} from '../../../types';

const fetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.FETCH, null, environmentLookupFn
);
const refetchSpec = createSpec(
  fetchRequestContract, fetchResponseContract,
  specTypes.REFETCH, null, environmentLookupFn
);
const mutationSpec = createSpec(
  mutationRequestContract, mutationResponseContract,
  specTypes.MUTATION, null, environmentLookupFn
);


export function useAccount(
  dataLocator: AccountVariables,
  config: FetchRefetchMutationConfig<Account, AccountVariables, AccountMutationData> = {}
) {
  const dataRefId = useDataRefId(null, environmentLookupFn);

  const defaultConfig = useMemo(() => ({

    fetch: config.fetch || {},

    refetch: config.refetch || {},

    mutation: config.mutation || {},
  }), [config]);

  const { fetch: fetchConfig, refetch, mutation } = defaultConfig;

  const fetch = useFetch(dataRefId, fetchSpec, dataLocator, fetchConfig) as [
    Account | undefined,
    unknown,
  ];
  const refetchFn = useRefetch(dataRefId, refetchSpec, refetch);

  const read = useCallback((
    param: ReadParameters<AccountVariables> = {},
    requestConfig?: ReadRequestConfig<Account, AccountVariables>
  ) => {
    refetchFn({
      variables: { ...dataLocator, ...param.variables },
    }, requestConfig);
  }, [dataLocator, refetchFn]);

  const updateFn = useMutation(dataRefId, mutationSpec, mutation.update);

  const update = useCallback((
    data: AccountMutationData,
    ...rest: Array<MutationRequestConfig<Account, AccountMutationData>>
  ) => {
    updateFn({ ...dataLocator }, data, ...rest);
  }, [dataLocator, updateFn]);

  const updateWithVariables = useCallback((
    data: AccountMutationData,
    variables: Partial<AccountVariables>,
    ...rest: Array<MutationRequestConfig<Account, AccountMutationData>>
  ) => {
    updateFn({ ...dataLocator, ...variables }, data, ...rest);
  }, [dataLocator, updateFn]);

  const [data, error] = fetch;

  return [{ data, error }, { read, update, updateWithVariables }];
}

export function useAccountWithDefaultSetting({
  aid,
  cid,
  uid,
}: AccountVariables['locator']['descriptor']) {
  const accountVariables = useMemo(() => ({
    locator: {
      descriptor: { cid, aid, uid },
    },
  }), [aid, uid, cid]);

  return useAccount(accountVariables);
}
